import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';

import { Gateway, UserPropertyEnum, UserPropertyValueEnum, UserRoleType } from '@vi-iot/client';

import { Gender, NO_VALUE } from '@pipes';

import { AppState } from '../app.state';

import { APPLICATION_ROLES } from '../auth';
import { hasMainDeviceRole } from '../installation-details';
import { InstallationsState } from '../installations';
import { GatewayViewModel, USER_DETAILS_ERROR_TYPES, UserProperty } from './user-details.model';
import { UserDetailsState } from './user-details.state';

export namespace UserDetailsSelectors {
  export const userDetails = (state: AppState): UserDetailsState => state.userDetails;
  export const installations = (state: AppState): InstallationsState => state.installations;

  export function getUserDetails(): MemoizedSelector<
    AppState,
    UserDetailsState,
    DefaultProjectorFn<UserDetailsState>
  > {
    return createSelector(userDetails, (state: UserDetailsState) => state);
  }

  export function isUserDetailsGatewayError(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      userDetails,
      (state: UserDetailsState) =>
        state.error && state.error.errorType === USER_DETAILS_ERROR_TYPES.GATEWAY_ERROR
    );
  }

  // eslint-disable-next-line
  export function getUserRole() {
    return createSelector(userDetails, (state: UserDetailsState) => {
      const iotRole: string = state?.user?.iotRole || '';
      let role: UserRoleType | string = '';

      if (iotRole) {
        // map uppercase role to previously used one
        const roleEntries: [string, UserRoleType][] = Object.entries(UserRoleType);
        const matchingEntry = roleEntries.find(
          ([_key, value]) => value.toLowerCase() === iotRole.toLowerCase()
        );

        if (matchingEntry) {
          role = matchingEntry[1];
        }
      }

      const id: string = state?.userDetails?.loginId || '';
      const properties: UserProperty[] = state?.userDetails?.properties;
      let type = NO_VALUE;
      if (properties && properties?.length) {
        const typeProp = properties.find((prop) => prop.name === UserPropertyEnum.IdentityType);
        type = typeProp?.value || '';
      }

      return {
        id,
        role,
        type,
      };
    });
  }

  // eslint-disable-next-line
  export function getUser() {
    return createSelector(userDetails, (state: UserDetailsState) => {
      const vFirstName: string = state?.user?.name.firstName;
      const vLastName: string = state?.user?.name.familyName;
      const firstName: string = state?.userDetails?.name?.firstName;
      const lastName: string = state?.userDetails?.name?.familyName;
      const gender: Gender = state?.userDetails?.gender;
      const customerNumber: string = state?.userDetails?.company?.id || '';

      return {
        firstName: firstName ?? vFirstName ?? '',
        lastName: lastName ?? vLastName ?? '',
        fullName: `${firstName} ${lastName}`,
        gender,
        customerNumber,
      };
    });
  }

  export function isUserActive(): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      userDetails,
      (state: UserDetailsState) => state.userDetails && state.userDetails.userState === 'ACTIVE'
    );
  }

  export function isEmailVerified(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      userDetails,
      (state: UserDetailsState) =>
        state.userDetails &&
        state.userDetails.properties &&
        !!state.userDetails.properties.find(
          (prop) =>
            prop.name === UserPropertyEnum.EmailVerified && prop.value === UserPropertyValueEnum.J
        )
    );
  }

  // eslint-disable-next-line
  export function getUserContact() {
    return createSelector(userDetails, (state: UserDetailsState) => {
      const vPhoneNumber: string = state?.user?.contacts.telephone;
      const vMobileNumber: string = state?.user?.contacts.mobile;
      const vFaxNumber: string = state?.user?.contacts.telefax;
      const vEmail: string = state?.user?.contacts.email;
      const vLanguage: string = state?.user?.locale?.toLowerCase();

      const telephone = state?.userDetails?.contacts?.telephone;
      const mobile = state?.userDetails?.contacts?.mobile;
      const telefax = state?.userDetails?.contacts?.telefax;
      const email: string = state?.userDetails?.contacts?.email;
      const language: string = state?.userDetails?.languageCode?.toLowerCase();

      return {
        phoneNumber: telephone ?? vPhoneNumber ?? '',
        mobileNumber: mobile ?? vMobileNumber ?? '',
        faxNumber: telefax ?? vFaxNumber ?? '',
        email: vEmail ?? email ?? '',
        language: language ?? vLanguage ?? '',
      };
    });
  }

  // eslint-disable-next-line
  export function getUserAddress() {
    return createSelector(userDetails, (state: UserDetailsState) => {
      const vStreet: string = state?.user?.address?.street;
      const vHouseNumber: string = state?.user?.address?.houseNumber;
      const vZip: string = state?.user?.address?.postalCode;
      const vCity: string = state?.user?.address?.city;
      const vCountry: string = state?.user?.address?.country?.toLowerCase();
      const vRegion = '';

      const street: string = state?.userDetails?.address?.street;
      const houseNumber: string = state?.userDetails?.address?.houseNumber;
      const zip: string = state?.userDetails?.address?.postalCode;
      const city: string = state?.userDetails?.address?.city;
      const country: string = state?.userDetails?.address?.countryCode?.toLowerCase();
      const region: string = state?.userDetails?.address?.locality;

      return {
        street: street ?? vStreet ?? '',
        houseNumber: houseNumber ?? vHouseNumber ?? '',
        zip: zip ?? vZip ?? '',
        city: city ?? vCity ?? '',
        country: country ?? vCountry ?? '',
        region: region ?? vRegion ?? '',
      };
    });
  }

  export function getUserGateways(): MemoizedSelector<
    AppState,
    GatewayViewModel,
    DefaultProjectorFn<GatewayViewModel>
  > {
    return createSelector(
      userDetails,
      installations,
      (state: UserDetailsState, installationsState: InstallationsState) => {
        const gateways: Gateway[] = [];

        if (state.installations && state.installations.length) {
          state.installations.forEach((installation) => {
            if (installation.gateways) {
              installation.gateways.forEach((gateway) => {
                const mainDevice = gateway.devices.find((dev) => hasMainDeviceRole(dev));
                const isColdSystem = installationsState.lockedInstallations.some(
                  (lockedInstallation) =>
                    lockedInstallation.gateways.find(
                      (lockedGateway) => lockedGateway.serial === gateway.serial
                    )
                );

                const evGateway = {
                  ...gateway,
                  marketingName: mainDevice?.marketingName,
                  installationAddress: installation.address,
                  isColdSystem,
                };

                gateways.push(evGateway);
              });
            }
          });
        }

        const vm: GatewayViewModel = {
          cursor: state.fetchMoreInstallationsCursor,
          gateways,
          isLoading: state.loadingInstallations,
          error: state.fetchInstallationsError,
        };

        return vm;
      }
    );
  }

  export function getUserCompanyName(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(userDetails, (state: UserDetailsState) => state?.company?.name || '');
  }

  export function userHasAccessToInvitations(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(userDetails, (state: UserDetailsState) =>
      state?.userDetails?.applicationRoles?.some(
        (role) => role === APPLICATION_ROLES.NEVIS_VITOGUIDE_INVITATION
      )
    );
  }

  export function getUserRoles(): MemoizedSelector<
    AppState,
    string[],
    DefaultProjectorFn<string[]>
  > {
    return createSelector(
      userDetails,
      (state: UserDetailsState) => state.userDetails.businessRoles
    );
  }

  export const getCompanyNumber = createSelector(
    userDetails,
    (state): string => state.company?.companyNumber
  );

  export const companyAddressForFlatRate = createSelector(
    getUserAddress(),
    (address) => `${address.street} ${address.houseNumber}, ${address.zip} ${address.city}`
  );
}
